import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import api from '../services/api';
import { Modal, Button } from 'react-bootstrap';

export type UserRole = 'admin' | 'instructor' | 'student' | 'volunteer';

export interface User {
  id: string;
  username: string;
  email: string;
  fullName?: string;
  phoneNumber?: string;
  role: UserRole;
  profileImage?: string | null;
  isActive?: boolean;
  createdAt?: Date;
  lastLogin?: Date;
};

type AuthContextType = {
  user: User | null;
  loading: boolean;
  isAdmin: boolean;
  login: (username: string, password: string, portal?: 'admin' | 'student') => Promise<boolean>;
  socialLogin: (provider: string) => Promise<boolean>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  hasRole: (role: UserRole) => boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Generate a unique session ID for this specific tab
const TAB_SESSION_ID = Math.random().toString(36).substring(2, 15);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showMultiTabModal, setShowMultiTabModal] = useState(false);
  const isAdmin = user?.role === 'admin';

  const login = async (username: string, password: string, portal: 'admin' | 'student' = 'student'): Promise<boolean> => {
    try {
      const res = await api.login(username, password);
      const token: string = res.token;
      const payload = res.user as { id: number; username: string; role: UserRole };
      
      // ADMIN RESTRICTION: Prevent admin from logging into student portal and vice versa
      if (portal === 'student' && payload.role === 'admin') {
        throw new Error('Admin accounts must login through the Admin portal.');
      }
      if (portal === 'admin' && payload.role !== 'admin') {
        throw new Error('Only administrators can access this portal.');
      }

      const userData: User = {
        id: String(payload.id),
        username: payload.username,
        email: '',
        role: payload.role,
        isActive: true,
        createdAt: new Date(),
      };

      // Set session identifiers
      localStorage.setItem('token', token);
      localStorage.setItem('userData', JSON.stringify(userData));
      localStorage.setItem('activeSessionId', TAB_SESSION_ID);
      
      setUser(userData);
      return true;
    } catch (e: any) {
      console.error('Login error:', e.message);
      throw e;
    }
  };

  const socialLogin = async (provider: string): Promise<boolean> => {
    try {
      const res: any = await api.socialLogin(provider);
      const token: string = res.token;
      const payload = res.user as { id: number; username: string; role: UserRole };
      const userData: User = {
        id: String(payload.id),
        username: payload.username,
        email: '',
        role: payload.role,
        isActive: true,
        createdAt: new Date(),
      };
      localStorage.setItem('token', token);
      localStorage.setItem('userData', JSON.stringify(userData));
      localStorage.setItem('activeSessionId', TAB_SESSION_ID);
      setUser(userData);
      return true;
    } catch (e) {
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    try { localStorage.removeItem('token'); } catch { }
    try { localStorage.removeItem('userData'); } catch { }
    try { localStorage.removeItem('activeSessionId'); } catch { }
    return Promise.resolve();
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = {
        ...user,
        ...userData,
        updatedAt: new Date()
      };
      setUser(updatedUser);
      localStorage.setItem('userData', JSON.stringify(updatedUser));
    }
  };

  const hasRole = (role: UserRole): boolean => {
    return user?.role === role || isAdmin;
  };

  // MULTI-TAB DETECTION
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'activeSessionId' && e.newValue && e.newValue !== TAB_SESSION_ID) {
        // Another tab has logged in or changed session
        if (user) {
          setShowMultiTabModal(true);
        }
      }
      if (e.key === 'token' && !e.newValue) {
        // Another tab logged out
        setUser(null);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [user]);

  // Load user data from localStorage on initial render.
  useEffect(() => {
    const hydrate = async () => {
      try {
        const token = localStorage.getItem('token');
        const savedUser = localStorage.getItem('userData');
        const activeSession = localStorage.getItem('activeSessionId');

        if (token && savedUser) {
          const parsed = JSON.parse(savedUser);
          
          // If there's an active session but it's not THIS tab, and we just opened
          // We can either take over or warn. For now, let's just let the first tab be the leader
          // if (!activeSession) localStorage.setItem('activeSessionId', TAB_SESSION_ID);

          setUser(parsed);
          
          try {
            const fullProfile = await api.users.getMe();
            if (fullProfile) {
              const updatedUser = {
                ...parsed,
                fullName: fullProfile.fullName || fullProfile.full_name,
                email: fullProfile.email,
                phoneNumber: fullProfile.phoneNumber || fullProfile.phone_number,
                profileImage: fullProfile.profileImage || fullProfile.profile_image
              };
              setUser(updatedUser);
              localStorage.setItem('userData', JSON.stringify(updatedUser));
            }
          } catch (e) {
            console.error('Failed to hydrate user profile:', e);
          }
        } else {
          if (!token) {
            try { localStorage.removeItem('userData'); } catch { }
          }
          setUser(null);
        }
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    hydrate();
  }, []);

  const contextValue: AuthContextType = {
    user,
    loading,
    isAdmin,
    login,
    socialLogin,
    logout,
    updateUser,
    hasRole
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      
      {/* Multi-tab session warning modal */}
      <Modal 
        show={showMultiTabModal} 
        onHide={() => {}} 
        backdrop="static" 
        keyboard={false} 
        centered
        className="session-modal"
      >
        <Modal.Header className="border-0 pb-0">
          <Modal.Title className="fw-bold text-danger">Session Conflict</Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-4">
          <p className="mb-0">You have logged in from another tab or window. To maintain security, this session is no longer active.</p>
          <p className="mt-2 small text-muted">Please logout from this tab and login again if you wish to continue here.</p>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="danger" className="rounded-pill px-4" onClick={() => {
            logout();
            setShowMultiTabModal(false);
            window.location.reload();
          }}>
            Logout & Reload
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .session-modal .modal-content {
          border-radius: 20px;
          border: none;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }
      `}</style>
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
